if (!customElements.get('byte-read-more')) {
  class ByteReadMore extends HTMLElement {
    constructor() {
      super();

      this.isExpanded = false;
      this.isAnimating = false;
      this.boundHandleClick = this.handleClick.bind(this);
      this.boundHandleResize = this.#handleResize.bind(this);
    }

    connectedCallback() {
      this.#cacheElements();
      this.#bindEvents();
      this.#applyCollapsedState();
      this.#updateToggleVisibility();
    }

    disconnectedCallback() {
      this.#unbindEvents();
    }

    handleClick(event) {
      event.preventDefault();
      if (this.isAnimating) return;
      this.toggle();
    }

    async toggle() {
      if (this.isExpanded) {
        await this.#collapse();
      } else {
        await this.#expand();
      }

      this.#updateButtonState();
    }

    #cacheElements() {
      this.wrapperElement = this.querySelector('[data-byte-read-more-wrapper]');
      this.contentElement = this.querySelector('[data-byte-read-more-content]');
      this.fadeElement = this.querySelector('[data-byte-read-more-fade]');
      this.toggleButton = this.querySelector('[data-byte-read-more-button]');
    }

    #bindEvents() {
      this.toggleButton?.addEventListener('click', this.boundHandleClick);
      window.addEventListener('resize', this.boundHandleResize);
    }

    #unbindEvents() {
      this.toggleButton?.removeEventListener('click', this.boundHandleClick);
      window.removeEventListener('resize', this.boundHandleResize);
    }

    #getCollapsedHeight() {
      const collapsedLines = Number(this.dataset.collapsedLines) || 4;
      const computedStyles = window.getComputedStyle(this.contentElement);
      const lineHeight = parseFloat(computedStyles.lineHeight) || 24;

      this.style.setProperty('--byte-read-more-line-height', `${lineHeight}px`);
      this.style.setProperty('--byte-read-more-collapsed-lines', String(collapsedLines));

      return lineHeight * collapsedLines;
    }

    #applyCollapsedState() {
      if (!this.wrapperElement || !this.contentElement) return;

      const collapsedHeight = this.#getCollapsedHeight();
      const hasOverflow = this.#hasOverflow(collapsedHeight);

      this.classList.toggle('is-expanded', this.isExpanded);
      this.contentElement.classList.toggle('is-collapsed', !this.isExpanded);

      if (this.isExpanded) {
        this.wrapperElement.style.maxHeight = 'none';
        this.wrapperElement.style.overflow = '';
        this.fadeElement?.classList.remove('is-visible');
      } else {
        this.wrapperElement.style.maxHeight = `${collapsedHeight}px`;
        this.wrapperElement.style.overflow = 'hidden';
        this.fadeElement?.classList.toggle('is-visible', hasOverflow);
      }
    }

    async #expand() {
      const startHeight = this.wrapperElement.offsetHeight;

      this.wrapperElement.style.maxHeight = 'none';
      const endHeight = this.contentElement.scrollHeight;
      this.wrapperElement.style.maxHeight = `${startHeight}px`;

      await this.#animateHeight(startHeight, endHeight, true);

      this.isExpanded = true;
      this.wrapperElement.style.maxHeight = 'none';
      this.wrapperElement.style.overflow = '';
      this.classList.add('is-expanded');
      this.contentElement.classList.remove('is-collapsed');
      this.fadeElement?.classList.remove('is-visible');
    }

    async #collapse() {
      const endHeight = this.#getCollapsedHeight();
      const startHeight = this.contentElement.scrollHeight;

      this.wrapperElement.style.maxHeight = `${startHeight}px`;
      this.wrapperElement.style.overflow = 'hidden';

      await this.#animateHeight(startHeight, endHeight, false);

      this.isExpanded = false;
      this.wrapperElement.style.maxHeight = `${endHeight}px`;
      this.classList.remove('is-expanded');
      this.contentElement.classList.add('is-collapsed');
      this.fadeElement?.classList.add('is-visible');
    }

    async #animateHeight(startHeight, endHeight, isExpanding) {
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        this.wrapperElement.style.maxHeight = isExpanding ? 'none' : `${endHeight}px`;
        return;
      }

      this.isAnimating = true;
      this.wrapperElement.style.overflow = 'hidden';

      if (isExpanding) {
        this.fadeElement?.classList.remove('is-visible');
      }

      const animation = this.wrapperElement.animate(
        {
          maxHeight: [`${startHeight}px`, `${endHeight}px`],
        },
        {
          duration: 320,
          easing: 'cubic-bezier(0.4, 0, 0.2, 1)',
          fill: 'forwards',
        }
      );

      await animation.finished.catch(() => {});

      this.isAnimating = false;
    }

    #hasOverflow(collapsedHeight) {
      return this.contentElement.scrollHeight > collapsedHeight + 1;
    }

    #updateToggleVisibility() {
      if (!this.contentElement || !this.toggleButton) return;

      const collapsedHeight = this.#getCollapsedHeight();
      const hasOverflow = this.#hasOverflow(collapsedHeight);

      this.toggleButton.hidden = !hasOverflow;

      if (!hasOverflow) {
        this.isExpanded = false;
        this.#applyCollapsedState();
      }

      this.#updateButtonState();
    }

    #updateButtonState() {
      if (!this.toggleButton) return;

      this.toggleButton.setAttribute('aria-expanded', String(this.isExpanded));
    }

    #handleResize() {
      if (!this.isExpanded) {
        this.#updateToggleVisibility();
        this.#applyCollapsedState();
      }
    }
  }

  customElements.define('byte-read-more', ByteReadMore);
}
